import { action, makeObservable, override, runInAction, toJS } from 'mobx';
import { SortDirection } from 'constants/SortDirection';
import { ScanType } from 'constants/ScanType';
import { destroy, EntityName, get, getCount, getDetailsForCorrectPagination, getPage, patch, post, buildWhereFilter } from 'stores/_services/dataService';
import { getConditions, getListConditions } from 'stores/_services/filterService';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import OrganizationStore from 'stores/organization/OrganizationStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';

const SEARCH_FIELDS = ['name'];

export default class OrganizationListStore extends BaseListStore<OrganizationStore> {
    public get items() {
        return super.items.map((item) => ({ ...item, organization: { ...item?.organization, name: item?.organization?.name ?? '' } }));
    }

    constructor() {
        super({
            itemStore: OrganizationStore,
            sortBy: 'name',
            sortDirection: SortDirection.ASC
        });

        makeObservable(this, {
            clearIds: override,
            getOrganizations: action
        });
    }

    async getOrganizations(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT, applyLimit = false): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                ...(!applyLimit && { limit: pageSize }),
                ...(!applyLimit && { skip: (pageNumber - 1) * pageSize }),
                where: conditions
            }
        };

        const { data } = await getPage(EntityName.ORGANIZATION_DETAILS, options);
        const totalCount = await getCount(EntityName.ORGANIZATION_DETAILS, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getOrganizationsForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const params = {
            filter: filter || this.filter,
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: SEARCH_FIELDS
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<OrganizationStore>({ params, entityName: EntityName.ORGANIZATION_DETAILS });

        this.updateStateForCorrectPagination<OrganizationStore>({ args, data, totalCount });
    }

    async getOrganizationData(id: string): Promise<void> {
        const { data } = await get(`${EntityName.ORGANIZATION_DETAILS}/${id}`);
        this._updateData(data);
        return data;
    }

    async existsOrganizationName(name: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name }
            }
        };
        const { data } = await get('organizations', options);
        return !!data.length;
    }

    async existsLocationName(name: string, organizationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, organizationId: organizationId }
            }
        };
        const { data } = await get('locations', options);
        return !!data.length;
    }

    async addOrganization(name: string, description: string): Promise<boolean> {
        const exists = await this.existsOrganizationName(name);
        if (exists) {
            return false;
        }
        const payload = {
            name: name,
            description: description
        };
        await post('organizations', payload);
        await this.getOrganizations({ reset: true });
        return true;
    }

    async editOrganization(id: string, name: string, description: string, oldName: string): Promise<boolean> {
        const exists = name !== oldName && (await this.existsOrganizationName(name));
        if (exists) {
            return false;
        }
        const payload = {
            name: name,
            description: description
        };
        await patch(`organizations/${id}`, payload);
        await this.getOrganizations({ reset: true });
        return true;
    }

    async deleteOrganizations(id?: string): Promise<void> {
        const conditions = getListConditions(id ? [id] : this.selectedIdList, this.filter);
        const options = {
            searchFields: SEARCH_FIELDS,
            where: conditions
        };
        await destroy('organizations', options);
        this.selectedIds = {};
        await this.getOrganizations({ reset: true });
    }

    async assignPolicyToOrganizations(policyId: string, deviceGroupId: string, organizationFilter: any): Promise<void> {
        const payload = {
            policyId,
            deviceGroupId,
            organizationFilter
        };

        await post(`policies/assignToOrganizations`, payload);
        await this.getOrganizations({ reset: true });
    }

    async removePolicyFromOrganizations(policyId: string, deviceGroupId: string, organizationFilter: any): Promise<void> {
        const payload = {
            policyId,
            deviceGroupId,
            organizationFilter
        };

        await post(`policies/removeFromOrganizations`, payload);
        await this.getOrganizations({ reset: true });
    }

    async scanOrganizations({ organizationIds, scanType, options }: { organizationIds: string[]; scanType: ScanType; options: any }): Promise<void> {
        let conditions: any[] = [];
        if (scanType === ScanType.FILTERED) {
            conditions = getConditions(filter);
        }
        if (organizationIds && organizationIds.length) {
            conditions.push({
                id: organizationIds
            });
        }

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });
        if (options?.extensions) {
            options.extensions = toJS(options.extensions).map((i: any, idx: number) => ({
                id: i.id,
                name: i.name,
                order: idx
            }));
        }
        await post('organizations/scan', { where, options });
    }
}
